import { z } from 'zod';
import { IsoDateTime, Uuid } from './common';
import { Spool } from './inventory';
import { Nozzle } from './slicer';

export const JobStatus = z.enum(['queued', 'printing', 'done', 'failed', 'cancelled']);
export type JobStatus = z.infer<typeof JobStatus>;
export const JOB_STATUS_LABELS: Record<JobStatus, string> = {
  queued: 'Na fila',
  printing: 'Imprimindo',
  done: 'Concluída',
  failed: 'Falhou',
  cancelled: 'Cancelada',
};

export const FailReason = z.enum([
  'adhesion',
  'spaghetti',
  'clog',
  'layer_shift',
  'power',
  'other',
]);
export type FailReason = z.infer<typeof FailReason>;
export const FAIL_REASON_LABELS: Record<FailReason, string> = {
  adhesion: 'Adesão',
  spaghetti: 'Espaguete',
  clog: 'Entupimento',
  layer_shift: 'Camada deslocada',
  power: 'Queda de energia',
  other: 'Outro',
};

/** Why a print outside orders and stock was made ("Lançar impressão passada"). */
export const JobPurpose = z.enum(['test', 'own_use', 'gift', 'marketing', 'other']);
export type JobPurpose = z.infer<typeof JobPurpose>;
export const JOB_PURPOSE_LABELS: Record<JobPurpose, string> = {
  test: 'Teste',
  own_use: 'Uso próprio',
  gift: 'Presente',
  marketing: 'Divulgação',
  other: 'Outro',
};

export const JobProblemCode = z.enum([
  'no_printer',
  'material',
  'short',
  'no_spool',
  'wrong_nozzle',
  'not_loaded',
]);
export const JobProblem = z.object({
  code: JobProblemCode,
  lineId: Uuid.nullable(),
  message: z.string().describe('pt-BR, ready to show.'),
  blocking: z.boolean().describe('Blocks "Iniciar" unless forced.'),
  fix: z
    .object({ spoolId: Uuid, spoolCode: z.string(), sealed: z.boolean() })
    .nullable()
    .describe('A spool of the same filament with enough grams.'),
  swap: z
    .object({
      printerId: Uuid,
      toSlot: z.string().describe('The slot that feeds the nozzle the profile asks for.'),
      spoolId: Uuid,
      spoolCode: z.string(),
      fromSlot: z.string(),
      otherSpoolCode: z
        .string()
        .nullable()
        .describe('The spool now in `toSlot`; it moves to `fromSlot`.'),
    })
    .nullable()
    .describe(
      '`wrong_nozzle`: "Trocar carretéis de slot" = `POST /printers/{printerId}/slots/{toSlot}/load` with ' +
        '`spoolId` (the two spools swap).',
    ),
});
export type JobProblem = z.infer<typeof JobProblem>;

export const JobFilament = z.object({
  id: Uuid,
  line: z.number().int(),
  filament: z.object({
    id: Uuid,
    name: z.string(),
    material: z.string(),
    colorHex: z.string().nullable(),
  }),
  nozzle: Nozzle.nullable(),
  estGrams: z.number(),
  needGrams: z.number().describe('Estimate + 5% margin ("folga").'),
  spool: Spool.nullable(),
  actualGrams: z.number().nullable(),
});
export type JobFilament = z.infer<typeof JobFilament>;

export const Job = z
  .object({
    id: Uuid,
    number: z.string().describe('"J-0001".'),
    status: JobStatus,
    printer: z.object({ id: Uuid, name: z.string() }).nullable(),
    position: z
      .number()
      .int()
      .nullable()
      .describe('1-based place in its printer lane (queued only).'),
    label: z.string().describe('"Vaso Ondas M ×2".'),
    product: z.object({ id: Uuid, name: z.string() }).nullable(),
    variant: z.object({ id: Uuid, name: z.string() }).nullable(),
    plateIndex: z.number().int(),
    plateCount: z.number().int(),
    units: z.number().int(),
    stockUnits: z.number().int().describe('Units going to Pronta entrega.'),
    batchId: Uuid,
    slicedFile: z.object({ id: Uuid, name: z.string(), version: z.number().int() }).nullable(),
    thumbnailUrl: z.string().nullable(),
    estSeconds: z.number().int(),
    estGrams: z.number(),
    dueOn: z.iso.date().nullable(),
    orders: z
      .array(
        z.object({
          id: Uuid,
          number: z.string(),
          units: z.number().int().describe('Units of this run that go to the order.'),
          shipBy: z.iso.date().nullable(),
        }),
      )
      .describe('Orders this run prints for ("P-2026-0122 (3) + estoque (9)").'),
    redoOf: z
      .object({ id: Uuid, number: z.string(), failPct: z.number().int().nullable() })
      .nullable(),
    startedAt: IsoDateTime.nullable(),
    expectedEndAt: IsoDateTime.nullable(),
    progressPct: z.number().int().nullable().describe('Printing: elapsed ÷ estimate.'),
    finishedAt: IsoDateTime.nullable(),
    actualSeconds: z.number().int().nullable(),
    actualGrams: z.number().nullable(),
    failPct: z.number().int().nullable(),
    failReason: FailReason.nullable(),
    wastedGrams: z.number().nullable(),
    goodUnits: z.number().int().nullable(),
    defectUnits: z.number().int().nullable(),
    realCostCentavos: z.number().int().nullable(),
    purpose: JobPurpose.nullable().describe(
      'Set for prints outside orders and stock (tests, own use, gifts…); null for order and stock runs.',
    ),
    note: z.string().nullable(),
    filaments: z.array(JobFilament),
    problems: z.array(JobProblem),
    createdAt: IsoDateTime,
  })
  .meta({ id: 'Job' });
export type Job = z.infer<typeof Job>;

export const JobList = z.object({ data: z.array(Job) });
export const JobQuery = z.object({
  status: z
    .string()
    .regex(/^[a-z_,]+$/)
    .optional()
    .describe('Comma-separated statuses; default `queued,printing`.'),
  printerId: Uuid.optional(),
  batchId: Uuid.optional(),
  finishedSince: IsoDateTime.optional().describe('Done or failed since (e.g. "Hoje").'),
  month: z
    .string()
    .regex(/^\d{4}-(0[1-9]|1[0-2])$/)
    .optional()
    .describe('Finished (done or failed) in this São Paulo month, newest first.'),
  failReason: FailReason.optional(),
  purpose: z
    .union([z.literal('any'), JobPurpose])
    .optional()
    .describe('Prints outside orders and stock: `any`, or one purpose.'),
});

export const JobDestination = z.object({
  orderItemId: Uuid,
  orderId: Uuid,
  orderNumber: z.string(),
  customer: z.string().nullable(),
  shipBy: z.iso.date().nullable(),
  needUnits: z.number().int().describe('Units not yet produced, in stock or in the queue.'),
});
export const JobDestinations = z.object({ data: z.array(JobDestination) });

export const CreateJobBody = z
  .object({
    variantId: Uuid,
    units: z.number().int().min(1).max(10_000),
    printerId: Uuid.nullable()
      .default(null)
      .describe('Defaults to the first compatible printer of the profile.'),
    note: z.string().trim().max(300).nullable().default(null),
  })
  .meta({ id: 'CreateJobBody' });
export type CreateJobBody = z.input<typeof CreateJobBody>;

/** When a print really happened, for prints marked after the fact. */
const PastDate = IsoDateTime.refine(
  (v) => new Date(v).getTime() <= Date.now() + 5 * 60_000,
  'A data não pode estar no futuro',
);

export const CreatePastJobBody = z
  .object({
    printerId: Uuid,
    label: z.string().trim().min(1).max(120).describe('What was printed, e.g. "Teste de encaixe".'),
    purpose: JobPurpose,
    result: z.enum(['done', 'failed']),
    failReason: FailReason.optional().describe('Required when `result` is `failed`.'),
    failPct: z
      .number()
      .int()
      .min(0)
      .max(100)
      .optional()
      .describe('How far it got, when it failed.'),
    finishedAt: PastDate.describe('When it ended (or failed).'),
    seconds: z
      .number()
      .int()
      .min(1)
      .max(30 * 24 * 3600)
      .describe('Real printing time.'),
    startedAt: PastDate.optional().describe('Defaults to `finishedAt` − `seconds`.'),
    lines: z
      .array(
        z.object({
          filamentId: Uuid,
          grams: z.number().min(0).max(100_000),
          nozzle: z.enum(['main', 'aux']).nullable().default(null),
          spoolId: Uuid.nullable()
            .optional()
            .describe(
              'Spool to deduct the grams from. Left out: the one loaded on the printer, else an opened ' +
                'one, as the queue suggests. `null`: do not deduct (the spool was weighed since).',
            ),
        }),
      )
      .min(1)
      .max(16),
    note: z.string().trim().max(500).nullable().default(null),
  })
  .refine((b) => b.result === 'done' || b.failReason !== undefined, {
    message: 'Informe o motivo da falha',
    path: ['failReason'],
  })
  .meta({ id: 'CreatePastJobBody' });
export type CreatePastJobBody = z.input<typeof CreatePastJobBody>;

export const UpdateJobBody = z.object({
  printerId: Uuid.nullable().optional(),
  spools: z
    .array(z.object({ lineId: Uuid, spoolId: Uuid.nullable() }))
    .max(32)
    .optional()
    .describe('Spool per filament line.'),
  note: z.string().trim().max(300).nullable().optional(),
});

export const MoveJobBody = z
  .object({
    direction: z.enum(['up', 'down']).optional(),
    position: z.number().int().min(0).optional().describe('0-based place in the lane.'),
    printerId: Uuid.nullable().optional().describe('Move to another printer lane (end of it).'),
  })
  .refine(
    (b) => b.direction !== undefined || b.position !== undefined || b.printerId !== undefined,
    { message: 'Send direction, position or printerId' },
  );

export const StartJobBody = z
  .object({ force: z.boolean().default(false).describe('Start despite blocking problems.') })
  .nullish();

/** "Já foi impressa": a run marked after the fact (queued or printing). */
const WhenFields = {
  startedAt: PastDate.optional().describe(
    'When it really started. With `finishedAt`, a queued job can be finished or failed directly; ' +
      'defaults to the recorded start, else `finishedAt` − `actualSeconds`.',
  ),
  finishedAt: PastDate.optional().describe(
    'When it really ended, if not now. `startNext` cannot be used with it.',
  ),
};

export const FinishJobBody = z.object({
  actualSeconds: z
    .number()
    .int()
    .min(0)
    .max(30 * 24 * 3600)
    .optional()
    .describe(
      'Real printing time; with `startedAt` and `finishedAt` it defaults to the difference.',
    ),
  grams: z
    .array(z.object({ lineId: Uuid, grams: z.number().min(0).max(100_000) }))
    .max(32)
    .describe('Real grams per filament line; lines left out use the estimate.'),
  goodUnits: z.number().int().min(0).max(10_000).optional().describe('Defaults to all units.'),
  defectUnits: z.number().int().min(0).max(10_000).default(0),
  startNext: z.boolean().default(false),
  goodTo: z
    .discriminatedUnion('kind', [
      z.object({ kind: z.literal('stock') }),
      z.object({ kind: z.literal('order'), orderItemId: Uuid }),
    ])
    .nullable()
    .default(null)
    .describe(
      '"Peças boas vão para": null = as planned; `stock` = an order run goes to Pronta entrega (the order ' +
        'prints the rest again); `order` = a stock run goes to an open order item of the same variant ' +
        '(`GET /jobs/{id}/destinations`). Single-plate runs only.',
    ),
  ...WhenFields,
});
export const FailJobBody = z.object({
  pct: z.number().int().min(0).max(100).describe('How much printed before failing.'),
  reason: FailReason,
  wastedGrams: z.number().min(0).max(100_000).optional().describe('Defaults to estimate × pct.'),
  note: z.string().trim().max(500).nullable().default(null),
  requeue: z.boolean().default(true).describe('Put a redo at the top of the queue.'),
  startNext: z.boolean().default(false),
  ...WhenFields,
});
export const JobResult = z.object({
  job: Job,
  next: Job.nullable().describe('The job started next, if any.'),
  redo: Job.nullable().describe('The redo queued after a failure.'),
  stockAdded: z.number().int().describe('Units added to Pronta entrega.'),
  nextBlocked: z
    .string()
    .nullable()
    .describe('Why the next job did not start when startNext was asked (e.g. a short spool).'),
});

export const QueueTimeline = z
  .object({
    now: IsoDateTime,
    printers: z.array(
      z.object({
        printer: z.object({ id: Uuid, name: z.string(), status: z.string() }),
        freeAt: IsoDateTime,
        backlogSeconds: z.number().int(),
        segments: z.array(
          z.object({
            jobId: Uuid,
            number: z.string(),
            label: z.string(),
            status: JobStatus,
            start: IsoDateTime,
            end: IsoDateTime,
            problem: z.boolean(),
          }),
        ),
      }),
    ),
    backlogSeconds: z
      .number()
      .int()
      .describe('Until the soonest-free active printer is free (feeds the lead time).'),
  })
  .meta({ id: 'QueueTimeline' });
export type QueueTimeline = z.infer<typeof QueueTimeline>;

// ---- Finished goods ("Pronta entrega") ---------------------------------------------------------

export const VariantStock = z.object({
  onHand: z.number().int(),
  reserved: z.number().int().describe('Held for open orders.'),
  available: z.number().int(),
  avgUnitCostCentavos: z.number().int().describe('Moving average of the real production cost.'),
});
export type VariantStock = z.infer<typeof VariantStock>;
export const StockAdjustBody = z.object({
  delta: z
    .number()
    .int()
    .min(-10_000)
    .max(10_000)
    .refine((v) => v !== 0, 'Use − or + to change the stock'),
  note: z.string().trim().max(300).nullable().default(null),
});
export const StockMovement = z.object({
  id: Uuid,
  at: IsoDateTime,
  kind: z.enum(['job', 'adjust', 'order_reserve', 'order_release', 'order_ship', 'order_return']),
  quantity: z.number().int(),
  reservedChange: z.number().int(),
  unitCostCentavos: z.number().int().nullable(),
  note: z.string().nullable(),
  actor: z.string(),
});
export const VariantStockDetail = VariantStock.extend({ movements: z.array(StockMovement) });
